import {
  Avatar,
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Rating,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppContainer from "../../../components/AppContainer";
import AppLoader from "../../../components/AppLoader";
import GlobalButton from "../../../components/CustomButton";
import ChangePasswordDialog from "../../../components/Dialog/ChangePasswordDialog";
import ProfileSaveDialog from "../../../components/Dialog/ProfileSaveDialog";
import Label from "../../../components/Label";
import NavigationDrawer from "../../../components/NavigationDrawer";
import TextInput from "../../../components/TextInputs";
import UploadImageAvatar from "../../../components/UploadImage";
import globalStyle from "../../../globalStyle";
import { setUserData } from "../../../lib/slices/userSlice";
import { getCompanySelectedServices } from "../../../services/company/serviceCategoryApiFn";
import {
  changeWorkerPassword,
  updateWorkerProfile,
} from "../../../services/worker/authenticationApiFn";
import { encryptPassword, handleOpenSnackBar } from "../../../utils";
import { updateWorkerProfileValidation } from "../../../utils/Validation";
import { baseUrl } from "../../../services";
import { getAddressDetailByPlaceId } from "../../../services/common/CommonApiFn";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import RomanianPhoneInput from "../../../components/romaniaPhoneInput";
import { t } from "i18next";
import { GetState, GetCity } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";

const useStyle = {
  selectStyle: {
    "&.MuiOutlinedInput-root .MuiSelect-outlined": {
      display: "flex",
      alignItems: "center",
      padding: "12px 14px",
    },
    "&.MuiOutlinedInput-root ": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #8692A6 `,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #8692A6 `,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #8692A6 `,
      },
    },
    "&.Mui-error ": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #d32f2f `,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #d32f2f `,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #d32f2f `,
      },
    },
  },
};

const WorkerAccountSetting = () => {
  // changepassword dialog
  const openCpDialog = useRef(null);
  // profile save dialog
  const opensPsDialog = useRef(null);
  // service save dialog
  const uploadRef = useRef();
  const opensServiceSaveDialog = useRef(null);

  const theme = useTheme();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const [location, setLocation] = useState(null);
  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [inputValues, setInputValues] = useState({
    image: "",
    name: "",
    email: "",
    contact: "",
    location: "",
    country: "Romania",
    city: "",
    state: "Bucharest",
    bio: "",
    gender: "",
    expertise: "",
    workerBgImages: [],
  });
  const [inputError, setInputError] = useState({
    image: false,
    name: false,
    email: false,
    isEmailValid: false,
    contact: false,
    location: false,
    country: false,
    city: false,
    state: false,
    bio: false,
    gender: false,
    expertise: false,
    bioGreaterThan50: false,
    workerBgImages: false,
    bioLessThan100: false,
  });
  const userData = useSelector((state) => state.userData.user);
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }, []);

  useEffect(() => {
    if (userData) {
      setLocation(userData?.location);
      setInputValues((prev) => ({
        ...prev,
        image: userData.profileImg,
        name: userData.name,
        email: userData.email,
        contact: userData.contact,
        location: userData.location,
        country: userData.country,
        city: userData.city,
        state: userData.state,
        bio: userData?.bio,
        gender: userData?.gender,
        expertise: userData?.expertise,
        workerBgImages: userData?.backgroundImages,
      }));
    }
  }, [userData]);

  const handleImageChange = async (event, typee) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (typee) {
        setInputValues((prevValues) => ({
          ...prevValues,
          workerBgImages: [...inputValues.workerBgImages, response?.data?.url],
        }));
        setInputError((prevError) => ({
          ...prevError,
          workerBgImages: false,
        }));
      } else {
        setInputValues((prevValues) => ({
          ...prevValues,
          image: response?.data?.url,
        }));
        setInputError((prevError) => ({
          ...prevError,
          image: false,
        }));
      }

      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeInputValues = (event) => {
    const { name, value } = event.target;

    if (name === "bio" && value.length > 350)
      return setInputError((error) => ({ ...error, bioLessThan100: true }));

    // Convert state value to lowercase for consistency with backend
    if (name === "state") {
      setInputValues((prev) => ({
        ...prev,
        city: "",
      }));
    }

    const processedValue =
      name === "state" || name === "city" ? value.toLowerCase() : value;

    setInputError((prevError) => ({
      ...prevError,
      [name]: false,
      isEmailValid: name === "email" ? false : inputError.isEmailValid,
      bioGreaterThan50: name === "bio" ? false : inputError.bioGreaterThan50,
      bioLessThan100: name === "bio" ? false : inputError.bioLessThan100,
    }));
    setInputValues((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  useEffect(() => {
    if (userData) {
      fetchAllServiceWithSubServices();
    }
  }, [userData]);

  const fetchAllServiceWithSubServices = async () => {
    try {
      setIsLoading(true);

      const response = await getCompanySelectedServices(userData.companyId._id);
      if (response?.data?.status === "success") {
        const allServicesData = response?.data?.data;

        const selectedServices = userData?.services || [];

        const tempArray = allServicesData.map((service) => {
          const selectedService = selectedServices.find(
            (selected) => selected.services === service._id
          );

          return {
            ...service,
            isSelected: !!selectedService,
            subservices: service.subservices.map((sub) => ({
              ...sub,

              isSelected: selectedService
                ? selectedService.subServices.includes(sub.serviceSubCategoryId)
                : false,
            })),
          };
        });

        setAllServices(tempArray);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectMainService = (index) => {
    const updatedServices = [...allServices];
    const mainService = updatedServices[index];
    mainService.isSelected = !mainService.isSelected;

    if (!mainService.isSelected) {
      mainService.subservices = mainService.subservices.map((sub) => ({
        ...sub,
        isSelected: false,
      }));
    }
    setAllServices(updatedServices);
  };

  const handleSelectSubService = (mainIndex, subIndex) => {
    const updatedServices = [...allServices];
    updatedServices[mainIndex].subservices[subIndex].isSelected =
      !updatedServices[mainIndex].subservices[subIndex].isSelected;
    setAllServices(updatedServices);
  };

  const renderRightPart = () => {
    return (
      <Stack
        gap={2}
        direction={"row"}
        alignItems={"center"}
        flex={1}
        justifyContent={"flex-end"}
      >
        <Box height={40}>
          <GlobalButton
            variant={"outlined"}
            btnText={"resetPassword"}
            styleProps={{ width: "auto" }}
            handleClickBtn={() => openCpDialog.current.openDialog()}
          />
        </Box>
        <Box width={100} height={40}>
          <GlobalButton
            variant={"contained"}
            btnText={"save"}
            styleProps={{ fontWeight: 400 }}
            handleClickBtn={() => {
              const validate = updateWorkerProfileValidation(
                inputValues,
                setInputError
              );
              if (!validate) {
                opensPsDialog.current.openDialog();
              }
            }}
          />
        </Box>
      </Stack>
    );
  };

  const handleSaveProfile = async (password) => {
    const longitude = location?.lng || location.coordinates[0];
    const latitude = location?.lat || location.coordinates[1];

    const locationObject = {
      addressLabel:
        inputValues.location.label || inputValues.location?.addressLabel,
      formatedAddress: location?.address || location?.formatedAddress,
      type: "Point",
      coordinates: [longitude, latitude],
    };

    let payload = {
      name: inputValues.name,
      contact: inputValues.contact,
      country: inputValues.country,
      city: inputValues.city,
      state: inputValues.state,
      expertise: inputValues.expertise,
      gender: inputValues.gender,
      location: locationObject,
      profileImg: inputValues.image,
      bio: inputValues.bio.trim(),
      backgroundImages: inputValues.workerBgImages,
      password,
    };
    try {
      setIsLoading(true);
      const response = await updateWorkerProfile(userData?._id, payload);
      if (response?.data.status === "success") {
        const data = response?.data?.data;
        const updatedData = { ...userData, ...data };
        localStorage.setItem("user-data", JSON.stringify(updatedData));
        dispatch(setUserData(updatedData));
        handleOpenSnackBar("success", response.data.message);
        opensPsDialog.current.closeDialog();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (data) => {
    try {
      const payload = {
        userId: userData._id,
        newPassword: encryptPassword(data.newPassword),
        oldPassword: encryptPassword(data.oldPassword),
        role: "Worker",
      };
      setIsLoading(true);
      const response = await changeWorkerPassword(payload);
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        openCpDialog.current.closeDialog();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenServiceSaveDialog = () => {
    const selectedServices = allServices
      .filter((service) => service.isSelected)
      .map((service) => ({
        services: service._id,
        subServices: service.subservices
          .filter((sub) => sub.isSelected)
          .map((sub) => sub.subServiceDetails._id),
      }));

    if (selectedServices.length === 0) {
      handleOpenSnackBar("error", t("selectAtLeastOneMainService"));
      return;
    }

    if (selectedServices.every((service) => service.subServices.length === 0)) {
      handleOpenSnackBar("error", t("selectAtLeastOneSubService"));
      return;
    }

    opensServiceSaveDialog.current.openDialog();
  };

  const handleUpdateServices = async (password) => {
    const selectedServices = allServices
      .filter((service) => service.isSelected)
      .map((service) => ({
        services: service._id,
        subServices: service.subservices
          .filter((sub) => sub.isSelected)
          .map((sub) => sub.subServiceDetails._id),
      }));

    let payload = {
      services: selectedServices,
      password,
    };

    try {
      setIsLoading(true);
      const response = await updateWorkerProfile(userData?._id, payload);
      if (response?.data.status === "success") {
        const data = response?.data?.data;
        const updatedData = { ...userData, ...data };
        localStorage.setItem("user-data", JSON.stringify(updatedData));
        dispatch(setUserData(updatedData));
        handleOpenSnackBar("success", response.data.message);
        opensServiceSaveDialog.current.closeDialog();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlaceDetails = async (object) => {
    try {
      setIsLoading(true);
      const response = await getAddressDetailByPlaceId(object.value.place_id);

      if (response?.data?.status === "success") {
        const data = response.data.data;
        const lat = data.result.geometry.location.lat;
        const lng = data.result.geometry.location.lng;
        console.log("Latitude:", lat, "Longitude:", lng);

        setLocation({
          address: data.result.formatted_address,
          lat,
          lng,
        });
      } else {
        handleOpenSnackBar("error", t("placeDetailsUnavailable"));
      }
    } catch (error) {
      handleOpenSnackBar("error", t("errorFetchingPlaceDetails"));
    } finally {
      setIsLoading(false);
    }
  };

  const getAllStates = async () => {
    try {
      const response = await GetState(181);
      setAllStates(response);
    } catch (error) {
      console.log(error, "error");
    }
  };

  const getAllCities = async () => {
    try {
      const stateName = inputValues.state?.toLowerCase();
      const findState = allStates.find(
        (val) => val.name?.toLowerCase() === stateName
      );

      const response = await GetCity(181, findState?.id);
      setAllCities(response);
    } catch (error) {
      console.log(error, "error");
    }
  };

  useEffect(() => {
    getAllStates();
  }, []);

  useEffect(() => {
    if (inputValues.state && allStates.length > 0) {
      getAllCities();
    }
  }, [inputValues.state, allStates]);

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <AppContainer
        primaryHeading={t("accountDetails")}
        secondaryHeading={t("personalDetailsInfo")}
        headProps={renderRightPart()}
        childrenProps={{
          minHeight: "80vh",
        }}
      >
        <Typography variant="h1" fontWeight={600} fontSize={"1.125rem"} mb={2}>
          {t("personalDetails")}
        </Typography>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            md={2.5}
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
            // justifyContent={"center"}
          >
            <Box mb={1}>
              <UploadImageAvatar
                error={inputError.image}
                image={inputValues.image}
                handleImageChange={handleImageChange}
              />
            </Box>
            <Box textAlign={"center"}>
              <Rating value={userData?.rating} precision={0.5} readOnly />
              <Typography>
                {userData?.rating}/5 ({userData?.reviews?.length || 0}{" "}
                {t("reviews")})
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={9.5}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Label
                  labelText={t("uploadBackgroundImages", {
                    maxImagesCount: 4,
                  })}
                  htmlFor={"full-name"}
                  required
                />
                {inputValues.workerBgImages.length < 4 && (
                  <Box
                    sx={{
                      border: "1px solid #00000024",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      alignSelf: "flex-start",
                      justifyContent: "center",
                      cursor: "pointer",
                      width: "50px",
                      height: "50px",
                    }}
                    onClick={() => uploadRef.current.click()}
                  >
                    <CameraAltIcon style={{ fontSize: "40px" }} />
                  </Box>
                )}
                <input
                  style={{ height: 0, width: 0, visibility: "hidden" }}
                  ref={uploadRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, "multiple")}
                />

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  {inputValues.workerBgImages.map((urlllll, inddd) => {
                    return (
                      <Box sx={{ position: "relative" }}>
                        <img
                          src={urlllll}
                          alt="bg"
                          style={{
                            width: "150px",
                            height: "150px",
                          }}
                        />
                        <Box
                          onClick={() => {
                            let arrrr = [...inputValues.workerBgImages];
                            arrrr.splice(inddd, 1);
                            setInputValues({
                              ...inputValues,
                              workerBgImages: arrrr,
                            });
                          }}
                          sx={{
                            position: "absolute",
                            height: "25px",
                            width: "25px",
                            background: "#fff",
                            border: "1px solid #00000024",
                            borderRadius: "50%",
                            top: "10px",
                            right: "10px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <DeleteIcon
                            style={{
                              color: "red",
                              fontSize: "20px",
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {inputError.workerBgImages && (
                  <FormHelperText sx={{ color: "custom.errorRed" }}>
                    {t("minimumImagesRequired", {
                      minimumImagesCount: 2,
                    })}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Label
                  labelText={t("fullName")}
                  required
                  htmlFor={"full-name"}
                />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"full-name"}
                  required
                  inputError={inputError.name}
                  helperText={inputError.name && t("fullNameRequired")}
                  placeholder={`${t("enter")} ${t("fullName")}`}
                  name="name"
                  value={inputValues.name}
                  onChange={handleChangeInputValues}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label
                  labelText={t("emailAddress")}
                  required
                  htmlFor={"email-address"}
                />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"email-address"}
                  required
                  readonly
                  inputError={inputError.email || inputError.isEmailValid}
                  helperText={
                    inputError.email
                      ? t("emailRequired")
                      : inputError.isEmailValid
                      ? t("invalidEmailFormat")
                      : null
                  }
                  placeholder={`${t("enter")} ${t("emailAddress")}`}
                  value={inputValues.email}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Label labelText={t("contact")} required htmlFor={"contact"} />
                <RomanianPhoneInput
                  inputError={inputError.contact}
                  value={inputValues.contact}
                  inputStyle={{
                    borderColor: inputError?.contact
                      ? theme.palette.error.main
                      : theme.palette.custom.slateGrey,
                  }}
                  buttonStyle={{
                    borderColor: inputError?.contact
                      ? theme.palette.error.main
                      : theme.palette.custom.slateGrey,
                  }}
                  onChange={(value) => {
                    setInputValues((prev) => ({ ...prev, contact: value }));
                    setInputError((err) => ({ ...err, contact: false }));
                  }}
                />
                {inputError.contact && (
                  <FormHelperText sx={{ color: "#d32f2f" }}>
                    {t("contactRequired")}
                  </FormHelperText>
                )}
                {/* <TextInput
                  id={"contact"}
                  styleProps={globalStyle.textField}
                  placeholder={"Enter contact"}
                  inputError={inputError.contact}
                  helperText={
                    inputError.contact && "Contact number is required"
                  }
                  required
                  name={"contact"}
                  value={inputValues.contact}
                  onChange={handleChangeInputValues}
                /> */}
              </Grid>
              <Grid item xs={12} md={4}>
                <Label
                  labelText={t("expertise")}
                  required
                  htmlFor={"expertise"}
                />
                <FormControl fullWidth error={inputError.expertise}>
                  <Select
                    name={"expertise"}
                    value={inputValues.expertise}
                    onChange={handleChangeInputValues}
                    sx={{
                      ...useStyle.selectStyle,
                    }}
                  >
                    <MenuItem value={""}>Select Expertise</MenuItem>
                    {["Junior", "Intermediate", "Expert"].map((val, _i) => (
                      <MenuItem key={_i} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                  </Select>
                  {inputError.expertise && (
                    <FormHelperText sx={{ ml: 0 }}>
                      {t("expertiseRequired")}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Label labelText={t("gender")} htmlFor={"gender"} required />
                <FormControl fullWidth error={inputError.gender}>
                  <Select
                    name={"gender"}
                    value={inputValues.gender}
                    onChange={handleChangeInputValues}
                    sx={{
                      ...useStyle.selectStyle,
                    }}
                  >
                    <MenuItem value={""}>{t("selectGender")}</MenuItem>
                    {["Male", "Female", "Other"].map((val, _i) => (
                      <MenuItem key={_i} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                  </Select>
                  {inputError.gender && (
                    <FormHelperText sx={{ ml: 0 }}>
                      {t("genderRequired")}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <Label labelText={t("country")} required htmlFor={"country"} />
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"country"}
                  readonly
                  value={"Romania"}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Label labelText={t("state")} htmlFor={"state"} required />
                <FormControl fullWidth error={inputError.state}>
                  <Select
                    name={"state"}
                    value={inputValues.state}
                    onChange={handleChangeInputValues}
                    sx={{
                      ...useStyle.selectStyle,
                    }}
                    readOnly={true}
                  >
                    <MenuItem value={""}>Select State</MenuItem>
                    {allStates?.map((val, _i) => (
                      <MenuItem key={val.id} value={val.name.toLowerCase()}>
                        {val.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {inputError.state && (
                    <FormHelperText sx={{ ml: 0 }}>
                      {t("stateRequired")}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Label labelText={t("city")} htmlFor={"city"} required />
                <FormControl fullWidth error={inputError.city}>
                  <Select
                    name={"city"}
                    value={inputValues.city}
                    onChange={handleChangeInputValues}
                    sx={{
                      ...useStyle.selectStyle,
                    }}
                  >
                    <MenuItem value={""}>Select City</MenuItem>
                    {allCities?.map((val, _i) => (
                      <MenuItem key={val.id} value={val.name.toLowerCase()}>
                        {val.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {inputError.city && (
                    <FormHelperText sx={{ ml: 0 }}>
                      {t("cityRequired")}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={12}>
                <Label
                  labelText={t("searchLocation")}
                  htmlFor={"location"}
                  required
                />
                <GooglePlacesAutocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
                  autocompletionRequest={{
                    types: ["(cities)"],
                    componentRestrictions: {
                      country: ["ro"],
                    },
                  }}
                  selectProps={{
                    styles: {
                      control: (provided, state) => ({
                        ...provided,
                        padding: "4px",
                        borderRadius: "6px",
                        borderColor: inputError.location
                          ? theme.palette.error.main
                          : theme.palette.custom.slateGrey,
                        boxShadow: state.isFocused
                          ? `0 0 0 1px ${theme.palette.custom.slateGrey}`
                          : "none",
                        "&:hover": {
                          borderColor: theme.palette.custom.slateGrey,
                        },
                      }),
                    },
                    value: location?.addresss,
                    onChange: (e) => {
                      setInputError((err) => ({ ...err, location: false }));
                      setInputValues((prev) => ({ ...prev, location: e }));
                      fetchPlaceDetails(e);
                    },
                  }}
                />

                {inputError.location && (
                  <FormHelperText sx={{ color: "#d32f2f" }}>
                    {t("selectYourLocation")}
                  </FormHelperText>
                )}
                <Label
                  labelText={t("selectedAddress")}
                  htmlFor={"location"}
                  labelProps={{ mt: 1 }}
                  required
                />

                <TextInput
                  styleProps={globalStyle.textField}
                  id={"location"}
                  placeholder={`${t("enter")} ${t("location")}`}
                  inputError={inputError.location}
                  helperText={inputError.location && t("locationRequired")}
                  required
                  readonly
                  // name={"location"}
                  value={
                    inputValues.location?.label ||
                    inputValues.location?.addressLabel
                  }
                  // onChange={handleChangeInputValues}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Label labelText={t("bio")} htmlFor={"bio"} required />
                  <Label labelText={`${inputValues.bio?.length || 0}/350`} />
                </Stack>
                <TextInput
                  styleProps={globalStyle.textField}
                  id={"bio"}
                  placeholder={`${t("enter")} ${t("bio")}`}
                  inputError={
                    inputError.bio ||
                    inputError.bioGreaterThan50 ||
                    inputError.bioLessThan100
                  }
                  helperText={
                    inputError.bio
                      ? t("bioRequired")
                      : inputError.bioGreaterThan50
                      ? t("bioMinLength", {
                          minLength: 150,
                        })
                      : inputError.bioLessThan100
                      ? t("bioMaxLength", {
                          maxLength: 350,
                        })
                      : null
                  }
                  required
                  multiline
                  rows={3}
                  name={"bio"}
                  value={inputValues.bio}
                  onChange={handleChangeInputValues}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Typography
          variant="h1"
          fontWeight={600}
          fontSize={"1.125rem"}
          mt={2}
          mb={2}
        >
          {t("companyDetails")}
        </Typography>
        <Grid container spacing={2} alignItems={"center"}>
          <Grid
            item
            xs={12}
            md={2.5}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <UploadImageAvatar
              image={userData?.companyId?.profileImg}
              canUpload={false}
            />
          </Grid>
          <Grid item xs={12} md={9.5}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Label labelText={`${"company"} ${t("name")}`} />
                <TextInput
                  styleProps={globalStyle.textField}
                  required
                  readonly
                  placeholder={`${"company"} ${t("name")}`}
                  value={userData?.companyId?.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label labelText={`${t("company")} ${t("emailAddress")}`} />
                <TextInput
                  styleProps={globalStyle.textField}
                  required
                  readonly
                  placeholder={`${t("company")} ${t("emailAddress")}`}
                  value={userData?.companyId?.email}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Label labelText={t("companyContact")} />
                <TextInput
                  styleProps={globalStyle.textField}
                  placeholder={t("companyContact")}
                  required
                  readonly
                  value={userData?.companyId?.contact}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Label labelText={t("companyLocation")} />
                <TextInput
                  styleProps={globalStyle.textField}
                  placeholder={t("companyLocation")}
                  required
                  readonly
                  value={userData?.companyId?.location?.addressLabel}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Typography variant="h1" fontWeight={600} fontSize={"1.125rem"} my={2}>
          {t("yourServices")}
        </Typography>

        {/* <Paper sx={{ p: 2 }}> */}
        <Stack spacing={2}>
          {allServices?.map((item, index) => (
            <Stack
              key={index}
              direction="column"
              spacing={1}
              bgcolor="#f1f1f1"
              p={1}
              border="1px solid #e9e8e8"
            >
              <Stack direction="row" spacing={1}>
                <Checkbox
                  checked={item.isSelected}
                  onChange={() => handleSelectMainService(index)}
                />
                <Stack direction="row" spacing={1}>
                  <Avatar
                    src={item.image}
                    sx={{
                      height: 70,
                      width: 70,
                      borderRadius: "6px",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      textTransform="capitalize"
                    >
                      {item.name[currentLanguage]}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item.description[currentLanguage]}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              {/* Subservices */}
              {item.isSelected &&
                item.subservices.map((sub, subIndex) => (
                  <Stack
                    key={subIndex}
                    direction="row"
                    spacing={1}
                    pl={4}
                    alignItems="center"
                    bgcolor={"#ffffff"}
                    py={1}
                  >
                    <Checkbox
                      checked={sub.isSelected}
                      onChange={() => handleSelectSubService(index, subIndex)}
                    />
                    <Avatar
                      src={sub.subServiceDetails.activeIcon}
                      sx={{
                        height: 40,
                        width: 40,
                        borderRadius: "6px",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        textTransform={"capitalize"}
                      >
                        {sub.subServiceDetails.name[currentLanguage]}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {sub.subServiceDetails.description[currentLanguage]}
                      </Typography>
                      <Stack
                        direction={{ md: "row" }}
                        gap={1}
                        alignItems={"center"}
                      >
                        <Typography variant="body2">
                          {t("serviceCharges")} :
                          {sub.subServiceDetails.percentage}%
                        </Typography>
                        <Typography variant="body2">
                          {t("companyPercentage")} :{sub.percentage}%
                        </Typography>
                        <Typography variant="body2">
                          {t("yourPercentage")} :
                          {100 -
                            sub.percentage -
                            sub.subServiceDetails.percentage}
                          %
                        </Typography>
                        {sub.subServiceDetails?.type === "fixed" && (
                          <Typography
                            variant="body2"
                            color="text.mediumGray"
                            fontWeight={600}
                          >
                            {t("serviceCharges")}: {sub?.price?.fixed || 0}
                          </Typography>
                        )}
                        {sub.subServiceDetails?.type === "area" && (
                          <Typography
                            variant="body2"
                            color="text.mediumGray"
                            fontWeight={600}
                          >
                            {t("perAreaCharges")}: {sub?.price?.area || 0}
                          </Typography>
                        )}

                        {sub.subServiceDetails?.type === "room+area" && (
                          <Typography
                            variant="body2"
                            color="text.mediumGray"
                            fontWeight={600}
                          >
                            {t("perAreaCharges")}: {sub?.price?.area || 0}{" "}
                            <br />
                            {t("perRoomCharges")}: {sub?.price?.room || 0}
                          </Typography>
                        )}
                        {sub.subServiceDetails?.type ===
                          "interior+exterior" && (
                          <Typography
                            variant="body2"
                            color="text.mediumGray"
                            fontWeight={600}
                          >
                            {t("interiorCharges")}: {sub?.price?.interior || 0}{" "}
                            <br />
                            {t("exteriorCharges")}: {sub?.price?.exterior || 0}
                          </Typography>
                        )}
                        {/* Duration */}
                        {sub.subServiceDetails?.type === "fixed" && (
                          <Typography
                            variant="body2"
                            color="text.mediumGray"
                            fontWeight={600}
                          >
                            {t("serviceDuration")}:{" "}
                            {sub?.subServiceDetails.duration?.fixed || 0}
                          </Typography>
                        )}
                        {sub.subServiceDetails?.type === "area" && (
                          <Typography
                            variant="body2"
                            color="text.mediumGray"
                            fontWeight={600}
                          >
                            {t("perAreaDuration")}:{" "}
                            {sub?.subServiceDetails.duration?.area || 0}
                          </Typography>
                        )}

                        {sub.subServiceDetails?.type === "room+area" && (
                          <Typography
                            variant="body2"
                            color="text.mediumGray"
                            fontWeight={600}
                          >
                            {t("perAreaDuration")}:{" "}
                            {sub?.subServiceDetails.duration?.area || 0} <br />
                            {t("perRoomDuration")}:{" "}
                            {sub?.subServiceDetails.duration?.room || 0}
                          </Typography>
                        )}
                        {sub.subServiceDetails?.type ===
                          "interior+exterior" && (
                          <Typography
                            variant="body2"
                            color="text.mediumGray"
                            fontWeight={600}
                          >
                            {t("interiorDuration")}:{" "}
                            {sub?.subServiceDetails.duration?.interior || 0}{" "}
                            <br />
                            {t("exteriorDuration")}:{" "}
                            {sub?.subServiceDetails.duration?.exterior || 0}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                ))}
            </Stack>
          ))}
        </Stack>

        <Stack my={3} alignItems={"flex-end"}>
          <GlobalButton
            btnText={"updateServices"}
            handleClickBtn={handleOpenServiceSaveDialog}
            styleProps={{
              width: 200,
              height: 40,
            }}
          />
        </Stack>
        {/* </Paper> */}
      </AppContainer>

      {/* Dialogs */}

      <ProfileSaveDialog
        ref={opensPsDialog}
        handleSaveProfile={handleSaveProfile}
      />
      <ProfileSaveDialog
        ref={opensServiceSaveDialog}
        handleSaveProfile={handleUpdateServices}
      />

      <ChangePasswordDialog
        ref={openCpDialog}
        handleChangePassword={handleChangePassword}
      />
    </NavigationDrawer>
  );
};

export default WorkerAccountSetting;
